import React, { useState, useEffect } from 'react';
import { apiClient } from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { 
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue 
} from '../../components/ui/select';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle
} from '../../components/ui/dialog';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from '../../components/ui/table';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger
} from '../../components/ui/dropdown-menu';
import { 
  Users, Plus, Search, MoreHorizontal, Edit, Trash2, Key, 
  Shield, UserCheck, UserX, Eye, EyeOff
} from 'lucide-react';
import { toast } from 'sonner';

const ROLE_LABELS = {
  "ADMIN": { label: "Administrador", color: "bg-red-500" },
  "SUPER_ADMIN": { label: "Super Admin", color: "bg-red-600" },
  "AUDITOR": { label: "Visitador", color: "bg-purple-500" },
  "UTILIZADOR": { label: "Utilizador", color: "bg-blue-500" },
  "INST_USER": { label: "Utilizador", color: "bg-blue-500" }
};

export default function GestaoUtilizadoresPage() {
  const [users, setUsers] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedUser, setSelectedUser] = useState(null);
  
  // Filters
  const [filterRole, setFilterRole] = useState('all');
  const [filterAtivo, setFilterAtivo] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  
  // Modals
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [resetPasswordModalOpen, setResetPasswordModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  
  // Form
  const [formData, setFormData] = useState({
    nome: '', email: '', password: '', role_codigo: 'UTILIZADOR'
  });
  const [newPassword, setNewPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  useEffect(() => {
    loadData();
  }, [filterRole, filterAtivo]);

  const loadData = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (filterRole !== 'all') params.append('role', filterRole);
      if (filterAtivo !== 'all') params.append('ativo', filterAtivo === 'active' ? 'true' : 'false');
      
      const res = await apiClient.get(`/admin/users?${params.toString()}`);
      setUsers(res.data.users || []);
      setStats(res.data.stats);
    } catch (error) {
      console.error('Error:', error);
      toast.error('Erro ao carregar utilizadores');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async () => {
    if (!formData.nome || !formData.email || !formData.password) {
      toast.error('Preencha todos os campos obrigatórios');
      return;
    }
    try {
      const fd = new FormData();
      fd.append('nome', formData.nome);
      fd.append('email', formData.email);
      fd.append('password', formData.password);
      fd.append('role_codigo', formData.role_codigo);
      
      await apiClient.post('/admin/users', fd);
      toast.success('Utilizador criado com sucesso');
      setCreateModalOpen(false);
      setFormData({ nome: '', email: '', password: '', role_codigo: 'UTILIZADOR' });
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao criar utilizador');
    }
  };

  const handleEdit = async () => {
    if (!selectedUser) return;
    try {
      const fd = new FormData();
      fd.append('nome', formData.nome);
      fd.append('email', formData.email);
      fd.append('role_codigo', formData.role_codigo);
      fd.append('ativo', formData.ativo);
      
      await apiClient.put(`/admin/users/${selectedUser.id}`, fd);
      toast.success('Utilizador atualizado');
      setEditModalOpen(false);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao atualizar');
    }
  };

  const handleResetPassword = async () => {
    if (!selectedUser || !newPassword) {
      toast.error('Introduza uma nova password');
      return;
    }
    try {
      const fd = new FormData();
      fd.append('new_password', newPassword);
      
      await apiClient.post(`/admin/users/${selectedUser.id}/reset-password`, fd);
      toast.success('Password alterada com sucesso');
      setResetPasswordModalOpen(false);
      setNewPassword('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao alterar password');
    }
  };

  const handleDelete = async () => {
    if (!selectedUser) return;
    try {
      await apiClient.delete(`/admin/users/${selectedUser.id}`);
      toast.success('Utilizador desativado');
      setDeleteModalOpen(false);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao desativar');
    }
  };

  const openEditModal = (user) => {
    setSelectedUser(user);
    setFormData({
      nome: user.nome,
      email: user.email,
      role_codigo: user.role_codigo || 'UTILIZADOR',
      ativo: user.ativo
    });
    setEditModalOpen(true);
  };

  const filteredUsers = users.filter(u => {
    if (!searchTerm) return true;
    const search = searchTerm.toLowerCase();
    return u.nome?.toLowerCase().includes(search) || u.email?.toLowerCase().includes(search);
  });

  const getRoleInfo = (roleCode) => ROLE_LABELS[roleCode] || ROLE_LABELS.UTILIZADOR;

  return (
    <div className="space-y-6" data-testid="gestao-utilizadores-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 dark:text-white flex items-center gap-3">
            <Users className="w-8 h-8 text-[#0066CC]" />
            Gestão de Utilizadores
          </h1>
          <p className="text-slate-500 dark:text-slate-400 mt-1">
            Gerir utilizadores, permissões e acessos ao sistema
          </p>
        </div>
        <Button onClick={() => setCreateModalOpen(true)} className="bg-[#0066CC] hover:bg-[#0052a3]" data-testid="create-user-btn">
          <Plus className="w-4 h-4 mr-2" />
          Novo Utilizador
        </Button>
      </div>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
          <Card>
            <CardContent className="pt-4">
              <div className="text-center">
                <p className="text-2xl font-bold text-slate-900 dark:text-white">{stats.total}</p>
                <p className="text-xs text-slate-500">Total</p>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <div className="text-center">
                <p className="text-2xl font-bold text-green-600">{stats.ativos}</p>
                <p className="text-xs text-slate-500">Ativos</p>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <div className="text-center">
                <p className="text-2xl font-bold text-slate-400">{stats.inativos}</p>
                <p className="text-xs text-slate-500">Inativos</p>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <div className="text-center">
                <p className="text-2xl font-bold text-red-600">{stats.admins}</p>
                <p className="text-xs text-slate-500">Admins</p>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <div className="text-center">
                <p className="text-2xl font-bold text-purple-600">{stats.visitadores || stats.auditores}</p>
                <p className="text-xs text-slate-500">Visitadores</p>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <div className="text-center">
                <p className="text-2xl font-bold text-blue-600">{stats.utilizadores}</p>
                <p className="text-xs text-slate-500">Utilizadores</p>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Filters */}
      <Card>
        <CardContent className="pt-4">
          <div className="flex flex-wrap gap-4">
            <div className="relative flex-1 min-w-[200px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Pesquisar por nome ou email..."
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
                className="pl-10"
                data-testid="search-users-input"
              />
            </div>
            <Select value={filterRole} onValueChange={setFilterRole}>
              <SelectTrigger className="w-[160px]" data-testid="filter-role">
                <SelectValue placeholder="Todos os Roles" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos os Roles</SelectItem>
                <SelectItem value="ADMIN">Administrador</SelectItem>
                <SelectItem value="AUDITOR">Visitador</SelectItem>
                <SelectItem value="UTILIZADOR">Utilizador</SelectItem>
              </SelectContent>
            </Select>
            <Select value={filterAtivo} onValueChange={setFilterAtivo}>
              <SelectTrigger className="w-[140px]" data-testid="filter-status">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos</SelectItem>
                <SelectItem value="active">Ativos</SelectItem>
                <SelectItem value="inactive">Inativos</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card>
        <CardContent className="pt-4">
          {loading ? (
            <div className="flex items-center justify-center h-48">
              <div className="w-8 h-8 border-2 border-slate-200 border-t-[#0066CC] rounded-full animate-spin"></div>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Nome</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Role</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Último Login</TableHead>
                  <TableHead className="text-right">Ações</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredUsers.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center py-12 text-slate-500">
                      <Users className="w-12 h-12 mx-auto mb-4 opacity-30" />
                      <p>Nenhum utilizador encontrado</p>
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredUsers.map(user => {
                    const roleInfo = getRoleInfo(user.role_codigo);
                    return (
                      <TableRow key={user.id}>
                        <TableCell>
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 bg-slate-100 dark:bg-slate-700 rounded-full flex items-center justify-center">
                              <span className="text-lg font-medium text-slate-600 dark:text-slate-300">
                                {user.nome?.charAt(0)?.toUpperCase()}
                              </span>
                            </div>
                            <span className="font-medium">{user.nome}</span>
                          </div>
                        </TableCell>
                        <TableCell>{user.email}</TableCell>
                        <TableCell>
                          <Badge className={`${roleInfo.color} text-white`}>
                            {roleInfo.label}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          {user.ativo ? (
                            <Badge variant="outline" className="border-green-500 text-green-600">
                              <UserCheck className="w-3 h-3 mr-1" /> Ativo
                            </Badge>
                          ) : (
                            <Badge variant="outline" className="border-slate-400 text-slate-500">
                              <UserX className="w-3 h-3 mr-1" /> Inativo
                            </Badge>
                          )}
                        </TableCell>
                        <TableCell>
                          <span className="text-sm text-slate-500">
                            {user.ultimo_login ? new Date(user.ultimo_login).toLocaleDateString('pt-PT') : '-'}
                          </span>
                        </TableCell>
                        <TableCell className="text-right">
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="sm" data-testid={`user-actions-${user.id}`}>
                                <MoreHorizontal className="w-4 h-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem onClick={() => openEditModal(user)}>
                                <Edit className="w-4 h-4 mr-2" /> Editar
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => { setSelectedUser(user); setResetPasswordModalOpen(true); }}>
                                <Key className="w-4 h-4 mr-2" /> Reset Password
                              </DropdownMenuItem>
                              <DropdownMenuItem 
                                onClick={() => { setSelectedUser(user); setDeleteModalOpen(true); }}
                                className="text-red-600"
                              >
                                <Trash2 className="w-4 h-4 mr-2" /> Desativar
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Create Modal */}
      <Dialog open={createModalOpen} onOpenChange={setCreateModalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Novo Utilizador</DialogTitle>
            <DialogDescription>Criar um novo utilizador no sistema</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Nome *</Label>
              <Input
                value={formData.nome}
                onChange={e => setFormData(p => ({ ...p, nome: e.target.value }))}
                placeholder="Nome completo"
                data-testid="input-nome"
              />
            </div>
            <div>
              <Label>Email *</Label>
              <Input
                type="email"
                value={formData.email}
                onChange={e => setFormData(p => ({ ...p, email: e.target.value }))}
                placeholder="email@exemplo.pt"
                data-testid="input-email"
              />
            </div>
            <div>
              <Label>Password *</Label>
              <div className="relative">
                <Input
                  type={showPassword ? 'text' : 'password'}
                  value={formData.password}
                  onChange={e => setFormData(p => ({ ...p, password: e.target.value }))}
                  placeholder="Mínimo 6 caracteres"
                  data-testid="input-password"
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="absolute right-1 top-1/2 -translate-y-1/2"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </Button>
              </div>
            </div>
            <div>
              <Label>Role</Label>
              <Select value={formData.role_codigo} onValueChange={v => setFormData(p => ({ ...p, role_codigo: v }))}>
                <SelectTrigger data-testid="select-role">
                  <SelectValue placeholder="Selecione..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="ADMIN">
                    <div className="flex items-center gap-2">
                      <Shield className="w-4 h-4 text-red-500" /> Administrador
                    </div>
                  </SelectItem>
                  <SelectItem value="AUDITOR">
                    <div className="flex items-center gap-2">
                      <Shield className="w-4 h-4 text-purple-500" /> Visitador
                    </div>
                  </SelectItem>
                  <SelectItem value="UTILIZADOR">
                    <div className="flex items-center gap-2">
                      <Users className="w-4 h-4 text-blue-500" /> Utilizador
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateModalOpen(false)}>Cancelar</Button>
            <Button onClick={handleCreate} className="bg-[#0066CC]" data-testid="submit-create-user">Criar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Modal */}
      <Dialog open={editModalOpen} onOpenChange={setEditModalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Editar Utilizador</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Nome</Label>
              <Input
                value={formData.nome}
                onChange={e => setFormData(p => ({ ...p, nome: e.target.value }))}
              />
            </div>
            <div>
              <Label>Email</Label>
              <Input
                type="email"
                value={formData.email}
                onChange={e => setFormData(p => ({ ...p, email: e.target.value }))}
              />
            </div>
            <div>
              <Label>Role</Label>
              <Select value={formData.role_codigo} onValueChange={v => setFormData(p => ({ ...p, role_codigo: v }))}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="ADMIN">Administrador</SelectItem>
                  <SelectItem value="AUDITOR">Visitador</SelectItem>
                  <SelectItem value="UTILIZADOR">Utilizador</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="ativo"
                checked={formData.ativo}
                onChange={e => setFormData(p => ({ ...p, ativo: e.target.checked }))}
                className="w-4 h-4"
              />
              <Label htmlFor="ativo">Ativo</Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditModalOpen(false)}>Cancelar</Button>
            <Button onClick={handleEdit} className="bg-[#0066CC]">Guardar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reset Password Modal */}
      <Dialog open={resetPasswordModalOpen} onOpenChange={setResetPasswordModalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reset Password</DialogTitle>
            <DialogDescription>
              Definir nova password para {selectedUser?.nome}
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <Label>Nova Password</Label>
            <div className="relative">
              <Input
                type={showPassword ? 'text' : 'password'}
                value={newPassword}
                onChange={e => setNewPassword(e.target.value)}
                placeholder="Nova password"
              />
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="absolute right-1 top-1/2 -translate-y-1/2"
                onClick={() => setShowPassword(!showPassword)}
              >
                {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </Button>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setResetPasswordModalOpen(false); setNewPassword(''); }}>Cancelar</Button>
            <Button onClick={handleResetPassword} className="bg-[#0066CC]">Alterar Password</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Modal */}
      <Dialog open={deleteModalOpen} onOpenChange={setDeleteModalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Desativar Utilizador</DialogTitle>
            <DialogDescription>
              Tem a certeza que deseja desativar o utilizador "{selectedUser?.nome}"?
              O utilizador não poderá aceder ao sistema.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteModalOpen(false)}>Cancelar</Button>
            <Button onClick={handleDelete} className="bg-red-600 hover:bg-red-700">Desativar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
